import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { ToastHost } from './components/kid/Toast';
import { DarkModeProvider } from './context/DarkModeContext';
import { LanguageProvider } from './context/LanguageContext';
import { useAppContext } from './context/AppContext';
import { i18n } from './i18n/i18n';

import Dashboard from './components/Dashboard';
import TaskBoard from './components/TaskBoard';
import AdminPanel from './components/AdminPanel';
import ApproveTasks from './components/parent/ApproveTasks';
import ChecklistEditor from './components/ChecklistEditor';
import TaskDetails from './components/TaskDetails';
import Header from './components/Header';
import NotificationCenter from './components/NotificationCenter';
import Login from './components/Login';
import MemberChooser from './components/MemberChooser';
import Rewards from './components/Rewards';
import CalendarPage from './components/CalendarPage';
import StatsPage from './components/stats/StatsPage';
import UserProfile from './components/UserProfile';
import Settings from './components/Settings';
import TailwindTest from './components/TailwindTest';
import OAuthCallback from './components/OAuthCallback';
import Signup from './components/Signup';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import FamilyDeletionPending from './components/FamilyDeletionPending';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import GlobalAdminLayout, { GlobalAdminIndexRedirect } from './components/GlobalAdminLayout';
import GlobalCatalogTools from './components/GlobalCatalogTools';
import GlobalCatalogTasks from './components/GlobalCatalogTasks';
import GlobalCatalogRewards from './components/GlobalCatalogRewards';
import GlobalCatalogRooms from './components/GlobalCatalogRooms';
import GlobalBetaCodes from './components/GlobalBetaCodes';
import { isGlobalCatalogAdmin } from './utils/catalogAdmin';
import { isMemberMode } from './services/supabaseClient';
import SubscriptionPage from './components/billing/SubscriptionPage';
import Paywall from './components/billing/Paywall';
import { FEATURES } from './constants/features';
import { PAYWALL_OPEN_PATHS, shouldShowPaywall } from './utils/billing';

import './App.css';
import './components/Button.css';

function ProtectedAdmin({ children }: { children: React.ReactNode }) {
  const { current_user } = useAppContext();
  
  // Wait for hydration: if user not yet loaded, don't redirect
  if (current_user === null) {
    return null;
  }
  if (!(current_user as any).is_admin) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

function ProtectedCatalogAdmin({ children }: { children: React.ReactNode }) {
  const { current_user, deviceLogout } = useAppContext() as any;
  const [signingOut, setSigningOut] = React.useState(false);
  if (current_user === null) return null;
  if (!isGlobalCatalogAdmin(current_user)) {
    return <Navigate to="/home" replace />;
  }
  // Master Admin is gated by a real Supabase Auth session (email allow-list) — a family PIN
  // session issues a different token that can't be verified as that Auth user, even when the
  // signed-in email matches the allow-list. Catch it here with a clear message instead of
  // letting the Edge Function calls fail with a confusing 401.
  if (isMemberMode()) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-3">
          Sign in with Google or email to continue
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Master Admin needs your original Google/email sign-in — you're currently signed in as a
          family PIN member on this device. Log out and sign back in with Google or email to
          continue.
        </p>
        <button
          type="button"
          disabled={signingOut}
          onClick={async () => {
            setSigningOut(true);
            try {
              await deviceLogout?.();
            } finally {
              setSigningOut(false);
            }
          }}
          className="btn btn-primary"
        >
          {signingOut ? 'Logging out…' : 'Log out'}
        </button>
      </div>
    );
  }
  return <>{children}</>;
}

function AppRoutes() {
  const { current_user, current_family, is_device_bound } = useAppContext() as any;
  const location = useLocation();
  // A new page starts at the top (step 8: Home → Tasks used to land at the bottom).
  React.useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // If no user is authenticated, show member chooser when device is bound; else login
  if (!current_user) {
    // Allow OAuth callback to render pre-auth
    if (location.pathname.startsWith('/oauth/callback')) {
      return <OAuthCallback />;
    }
    if (location.pathname.startsWith('/signup')) {
      return <Signup />;
    }
    if (location.pathname.startsWith('/forgot-password')) {
      return <ForgotPassword />;
    }
    if (location.pathname.startsWith('/reset-password')) {
      return <ResetPassword />;
    }
    return is_device_bound ? <MemberChooser /> : <Login />;
  }

  // Master admin is decoupled from the family app: a catalog admin can use it directly,
  // in its own shell, without a family loaded and without going through onboarding.
  if (location.pathname.startsWith('/global-admin')) {
    return (
      <ProtectedCatalogAdmin>
        <Routes>
          <Route path="/global-admin" element={<GlobalAdminLayout />}>
            <Route index element={<GlobalAdminIndexRedirect />} />
            <Route path="tools" element={<GlobalCatalogTools />} />
            <Route path="tasks" element={<GlobalCatalogTasks />} />
            <Route path="rewards" element={<GlobalCatalogRewards />} />
            <Route path="rooms" element={<GlobalCatalogRooms />} />
            <Route path="beta-codes" element={<GlobalBetaCodes />} />
          </Route>
        </Routes>
      </ProtectedCatalogAdmin>
    );
  }

  // The whole family is waiting to be deleted: only Restore (admins) or Log out.
  if ((current_user as any).family_deletion) {
    return <FamilyDeletionPending />;
  }

  // If no family is loaded yet, show loading
  if (!current_family) {
    return <div>{i18n.t('common.loading')}</div>;
  }

  // User is authenticated, show main app
  // OnboardingWizard.onSave() sets families.onboarding_completed_at once the wizard finishes.
  // (Previously inferred from members.length <= 1, which permanently traps any family that
  // finishes onboarding with a single adult and no other members — the default state.)
  const needs_onboarding = !current_family?.onboarding_completed_at;
  const onboardingBypass =
    location.pathname.startsWith('/onboarding') || location.pathname.startsWith('/global-admin');
  if (needs_onboarding && !onboardingBypass) {
    return <Navigate to="/onboarding" replace />;
  }

  // Payments (behind FEATURES.payments): access has ended. Members see the paywall; a grown-up
  // with admin access can still open the Subscription page, Settings and their profile.
  if (shouldShowPaywall((current_family as any).billing, FEATURES.payments)) {
    if (!current_user.is_admin) return <Paywall />;
    if (!PAYWALL_OPEN_PATHS.some((p) => location.pathname.startsWith(p))) return <Navigate to="/subscription" replace />;
  }

  return (
    <div className="app">
      <Header />
      <NotificationCenter />
      <main className="main-content w-full min-w-0 !pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] md:!pb-[calc(6.5rem+env(safe-area-inset-bottom,0px))] md:max-w-[1200px] md:mx-auto">
        <Routes>
          <Route path="/login" element={<Navigate to="/home" replace />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/signup" element={<Navigate to="/home" replace />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/home" element={<Dashboard />} />
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />
          <Route path="/tasks" element={<TaskBoard />} />

          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/rewards" element={<Rewards />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/onboarding" element={<OnboardingWizard />} />
          <Route path="/admin" element={<ProtectedAdmin><AdminPanel /></ProtectedAdmin>} />
          {FEATURES.payments && <Route path="/subscription" element={<ProtectedAdmin><SubscriptionPage /></ProtectedAdmin>} />}
          <Route path="/approve" element={<ProtectedAdmin><ApproveTasks /></ProtectedAdmin>} />
          {/* /global-admin is handled earlier, in its own shell (decoupled from the family app) */}
          <Route path="/checklist/:taskId" element={<ProtectedAdmin><ChecklistEditor /></ProtectedAdmin>} />
          <Route path="/tailwind-test" element={<TailwindTest />} />
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-app-bg dark:bg-gray-900 text-app-text dark:text-gray-100">
      <DarkModeProvider>
        <LanguageProvider>
          <AppProvider>
            <Router>
              <AppRoutes />
              <ToastHost />
            </Router>
          </AppProvider>
        </LanguageProvider>
      </DarkModeProvider>
    </div>
  );
}

export default App;
